import React, {useCallback, useMemo} from 'react';
import {makeStyles} from "@mui/styles";
import {Divider, MenuItem, TextField} from "@mui/material";
import {KeyboardArrowDown} from '@mui/icons-material';
import classNames from "classnames";
import {MuiRFF, useForm} from '../../form';
import {useFormOld} from "../form";


/**
 * The Menu props for blurring the dropdown after its been opened and to bring the menu to the front to prevent
 * its edges being clipped.
 */
const defaultMenuProps = () => ({
    TransitionProps: {
        onEnter: () => {
            setTimeout(() => {
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            }, 500);
        },
    },
    PaperProps: {
        elevation: 4,
        style: {
            transform: 'translate3d(0, 0, 0)'
        }
    },
    MenuListProps: {
        classes: {},
    },
    anchorOrigin: {
        vertical: "bottom",
        horizontal: "center"
    },
})

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        color: theme.vars.palette.background.paper,
        boxShadow: 'none',
        // boxShadow: props => props.shadow ? `0px 7px 7px ${theme.vars.palette['app-specific'].shadow}` : 'none',
    },
    icon: {
        cursor: props => (!props.data || !props.data.length) ? 'not-allowed' : 'pointer',
        marginRight: 4,
        marginLeft: 4,
        width: '1em',
        height: '1em',
    },
    innerInput: {
        fontSize: props => props.fontSize,
    },
}));

export const OutlinedDropdown = ({
                                     placeholder = 'type...',
                                     label,
                                     ariaLabel = 'input',
                                     value = '',
                                     onChange,
                                     name = label,
                                     type,
                                     fullWidth = true,
                                     data,
                                     shadow = true,
                                     fontSize = 15,
                                     disabled,
                                     ...props
                                 }) => {
    const classes = useStyles({shadow, data, fontSize});
    const {size} = props;


    return (
        <>
            <TextField
                select
                label={label}
                disabled={!data || !data.length || disabled}
                fullWidth={fullWidth}
                type={type}
                variant={'outlined'}
                name={name}
                value={value}
                SelectProps={{
                    IconComponent: (props) => (
                        <>
                            {size !== 'small' &&
                                <Divider className={classNames('select-divider', props.className)} orientation={'vertical'}/>}
                            <KeyboardArrowDown className={classNames([props.className, classes.icon])}/>
                        </>
                    ),
                    renderValue: (value => {
                        return <p>{(value.label ?? value.name ?? value.title ?? value.description ?? value) ?? ''}</p>
                    }),
                    MenuProps: defaultMenuProps(),
                    ...(props.SelectProps ?? {}),
                }}
                onChange={(e) => onChange(e.target.value)}
                className={classes.input}
                placeholder={placeholder}
                classes={{
                    root: 'text-left',
                }}
                inputProps={{'aria-label': ariaLabel, className: classes.innerInput}}
                {...props}
            >
                {data && data?.map(item => (
                    <MenuItem
                        className={item.className ?? ''}
                        value={item.value}
                        key={item.id}
                        disabled={item.disabled}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </TextField>
        </>
    );
}


export const OutlinedDropdownForm = ({
                                         placeholder = 'select...',
                                         label,
                                         ariaLabel = 'input',
                                         required = false,
                                         fullWidth = true,
                                         helperText,
                                         fieldProps,
                                         InputLabelProps,
                                         renderOptions,
                                         renderValue,
                                         onChange,
                                         name,
                                         data,
                                         multiple = false,
                                         shadow = true,
                                         disabled,
                                         ...props
                                     }) => {
    const classes = useStyles({data});
    const oldFormApi = useFormOld();
    const formApi = useForm();

    const defaultRenderValue = useCallback((value) => {
        if (multiple)
            return (
                <p className='text-wrap'>
                    {
                        value.map(e =>
                            <span key={e.label ?? e.name ?? e.title} className='mr-3'>
                                {e.label ?? e.name ?? e.title}
                            </span>
                        )
                    }
                </p>
            );
        return (
            <p>
                {(value.label ?? value.name ?? value.title ?? value.description ?? value) ?? ''}
            </p>
        )
    }, [multiple])

    const menuProps = useMemo(() => defaultMenuProps(), [])

    return (
        <>
            <MuiRFF.TextField
                select
                disabled={!data || !data.length || disabled}
                fullWidth={fullWidth}
                required={required}
                label={label}
                variant={'outlined'}
                name={name}
                multiple={multiple}
                helperText={helperText}
                fieldProps={fieldProps}
                InputLabelProps={InputLabelProps}
                onChange={(e) => {
                    if (onChange)
                        onChange(e)
                    const api = formApi ?? oldFormApi;
                    if (api) {
                        api.change(name, e.target.value)
                    }
                }}
                SelectProps={{
                    renderValue: renderValue ?? defaultRenderValue,
                    MenuProps: menuProps,
                    ...(props.SelectProps ?? {}),
                    autoWidth: false,
                    IconComponent: (props) => (
                        <>
                            <Divider className={classNames('select-divider', props.className)} orientation={'vertical'}/>
                            <KeyboardArrowDown className={classNames([props.className, classes.icon])}/>
                        </>
                    ),
                }}
                {...props}
            >
                {
                    data && data !== '' &&
                    data.map(item => (
                        <MenuItem
                            key={item.id}
                            className={item.className ?? ''}
                            value={item.value}
                            disabled={item.disabled}
                        >
                            {!!renderOptions ? renderOptions(item) : item.label}
                        </MenuItem>
                    ))
                }
            </MuiRFF.TextField>
        </>
    );
}
